/*
 * Innovist hero slideshow (sections/innovist-slideshow.liquid).
 *
 * Mirrors Innovist's Flickity setup (wrapAround, cellAlign centre,
 * friction 0.8, selectedAttraction 0.14, dragThreshold 7, autoplay that
 * keeps running on hover but stops for good once the visitor touches,
 * drags or uses the dots).
 *
 * Loaded with `defer`: the section renders the before/after copies of the
 * slides itself (cells with [data-clone]) and positions the track with CSS,
 * so the first paint does not depend on this file.
 */
(() => {
  if (window.invHeroInit) return;
  window.invHeroInit = true;

  const FRICTION = 0.8;
  const ATTRACTION = 0.14;
  const DRAG_THRESHOLD = 7;

  const init = (root) => {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';

    const viewport = root.querySelector('.inv-hero__viewport');
    const track = root.querySelector('.inv-hero__track');
    const prerendered = !!track.querySelector('.inv-hero__cell[data-clone]');
    const originals = [...track.children].filter((cell) => !cell.hasAttribute('data-clone'));
    const dots = [...root.querySelectorAll('.inv-hero__dot')];
    const count = originals.length;
    if (count < 2) {
      if (originals[0]) originals[0].classList.add('is-selected');
      return;
    }

    const fade = root.dataset.fade === 'true';

    // Slide mode: a full copy of the slides on each side, so neighbours
    // always exist and the row can wrap seamlessly in either direction.
    // The section normally renders the copies itself; cloning here only
    // covers markup that did not. Fade mode keeps the slides stacked, so no
    // copies are needed.
    const clone = (cell, eager) => {
      const copy = cell.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.removeAttribute('data-shopify-editor-block');
      if (eager) copy.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'eager'));
      return copy;
    };
    if (!fade && !prerendered) {
      const before = originals.map((cell, i) => clone(cell, i === count - 1));
      const after = originals.map((cell, i) => clone(cell, i === 0));
      track.prepend(...before);
      track.append(...after);
    }
    const cells = fade ? originals : [...track.children];
    const copyOffset = fade ? 0 : 1;
    const cellAt = (i) => cells[((i % cells.length) + cells.length) % cells.length];

    let index = copyOffset * count; // first real slide
    let fadeIndex = index;
    let x = 0;
    let velocity = 0;
    let animating = false;
    let restingFrames = 0;
    let cw = 0;
    let base = 0;
    let drag = null;

    const measure = () => {
      cw = fade ? viewport.clientWidth : cells[0].getBoundingClientRect().width;
      base = fade ? 0 : (viewport.clientWidth - cw) / 2;
    };
    // With server-rendered copies the section's CSS already holds the track at
    // the resting position, so the first measurement (a forced layout of the
    // whole page when this runs at DOMContentLoaded) waits until the first
    // move: autoplay tick, dot/arrow click, drag or resize.
    const ensureMeasured = () => {
      if (cw) return;
      measure();
      x = targetOf(index);
    };
    const targetOf = (i) => -i * cw;
    // Flickity fade: the two slides around the current position share the
    // opacity by progress; every other slide is hidden.
    const renderFade = () => {
      let a = fadeIndex;
      let b = index;
      if (drag && drag.active) {
        a = Math.round(-x / cw);
        b = a + (-x / cw - a >= 0 ? 1 : -1);
      }
      let progress = a === b ? 1 : (-x - a * cw) / ((b - a) * cw);
      progress = Math.min(Math.max(progress, 0), 1);
      const A = cellAt(a);
      const B = cellAt(b);
      cells.forEach((cell) => {
        let opacity = 0;
        if (cell === B) opacity = progress;
        if (cell === A) opacity = a === b ? 1 : 1 - progress;
        cell.style.opacity = opacity;
      });
    };
    const render = (is3d) => {
      if (fade) return renderFade();
      const value = Math.round(base + x);
      track.style.transform = is3d ? `translate3d(${value}px,0,0)` : `translateX(${value}px)`;
    };
    // Keep the selected index in the middle copy; shifting by a whole set
    // is invisible because the copies are identical.
    const normalize = () => {
      const shift = Math.floor(index / count) - copyOffset;
      if (!shift) return;
      const px = shift * count * cw;
      index -= shift * count;
      fadeIndex -= shift * count;
      x += px;
      if (drag) {
        drag.startX += px;
        drag.dragX += px;
        if (drag.prevX !== undefined) drag.prevX += px;
      }
    };
    const markSelected = () => {
      const real = ((index % count) + count) % count;
      const selected = cellAt(index);
      cells.forEach((cell) => cell.classList.toggle('is-selected', cell === selected));
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === real);
        if (i === real) dot.setAttribute('aria-current', 'step');
        else dot.removeAttribute('aria-current');
      });
    };

    const frame = () => {
      if (drag && drag.active) {
        velocity += drag.dragX - x - velocity;
      } else {
        velocity += (targetOf(index) - x) * ATTRACTION;
      }
      const previous = x;
      x += velocity;
      velocity *= 1 - FRICTION;
      render(true);
      if (!drag && Math.round(x * 100) === Math.round(previous * 100)) restingFrames++;
      if (restingFrames > 2) {
        animating = false;
        fadeIndex = index;
        render(false);
        return;
      }
      requestAnimationFrame(frame);
    };
    const animate = () => {
      restingFrames = 0;
      if (animating) return;
      animating = true;
      requestAnimationFrame(frame);
    };
    const select = (target) => {
      ensureMeasured();
      if (fade && !(drag && drag.active)) fadeIndex = Math.round(-x / cw);
      index = target;
      normalize();
      markSelected();
      animate();
    };

    // Autoplay (Flickity Player): fixed interval, not paused on hover,
    // paused while the tab is hidden, stopped by any interaction.
    const speed = Number(root.dataset.speed) || 6000;
    let state = root.dataset.autoplay === 'true' ? 'playing' : 'stopped';
    let timer = null;
    const tick = () => {
      clearTimeout(timer);
      if (state !== 'playing') return;
      timer = setTimeout(() => {
        select(index + 1);
        tick();
      }, speed);
    };
    const stopPlayer = () => {
      state = 'stopped';
      clearTimeout(timer);
    };
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && state === 'playing') {
        state = 'paused';
        clearTimeout(timer);
      } else if (!document.hidden && state === 'paused') {
        state = 'playing';
        tick();
      }
    });

    dots.forEach((dot) =>
      dot.addEventListener('click', () => {
        stopPlayer();
        const real = ((index % count) + count) % count;
        let delta = Number(dot.dataset.dot) - real;
        if (Math.abs(delta + count) < Math.abs(delta)) delta += count;
        else if (Math.abs(delta - count) < Math.abs(delta)) delta -= count;
        select(index + delta);
      })
    );

    const arrows = [...root.querySelectorAll('.inv-hero__arrow')];
    arrows.forEach((arrow) =>
      arrow.addEventListener('click', () => {
        stopPlayer();
        select(index + (arrow.classList.contains('inv-hero__arrow--prev') ? -1 : 1));
      })
    );

    // ----- drag / swipe -----
    let preventClick = false;
    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('.inv-hero__dots, .inv-hero__arrow')) return;
      if (event.pointerType === 'mouse') event.preventDefault();
      stopPlayer();
      ensureMeasured();
      preventClick = false;
      drag = { active: false, id: event.pointerId, px: event.clientX, startX: x, dragX: x };
      viewport.classList.add('is-pointer-down');
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const moveX = event.clientX - drag.px;
      if (!drag.active) {
        if (Math.abs(moveX) <= DRAG_THRESHOLD) return;
        drag.active = true;
        preventClick = true;
        drag.startX = x;
        drag.dragX = x;
        animate();
      }
      drag.prevX = drag.dragX;
      drag.dragX = drag.startX + moveX;
      drag.moveTime = Date.now();
    });
    const end = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const d = drag;
      viewport.classList.remove('is-pointer-down');
      drag = null;
      if (!d.active) return;
      if (fade) fadeIndex = Math.round(-x / cw);
      // Select the slide nearest to where the flick would come to rest,
      // boosting to the neighbour on a quick short flick.
      const resting = x + velocity / FRICTION;
      let best = index;
      let bestDist = Infinity;
      for (let i = index - count; i <= index + count; i++) {
        const dist = Math.abs(resting - targetOf(i));
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      if (best === index && d.prevX !== undefined && Date.now() - d.moveTime <= 100) {
        const distance = -d.dragX - index * cw;
        const delta = d.prevX - d.dragX;
        if (distance > 0 && delta > 0) best += 1;
        else if (distance < 0 && delta < 0) best -= 1;
      }
      select(best);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    viewport.addEventListener(
      'click',
      (event) => {
        if (preventClick) {
          event.preventDefault();
          event.stopPropagation();
          preventClick = false;
        }
      },
      true
    );
    viewport.addEventListener('dragstart', (event) => event.preventDefault());

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        measure();
        x = targetOf(index);
        velocity = 0;
        render(false);
      }, 100);
    });

    markSelected();
    if (fade || !prerendered) {
      measure();
      x = targetOf(index);
      render(false);
    }
    tick();
  };

  document.querySelectorAll('.inv-hero').forEach(init);
  document.addEventListener('shopify:section:load', (event) => init(event.target.querySelector('.inv-hero')));
})();
